import { Button, Flex, Text } from '@chakra-ui/react';
import { useSendMessage } from '@outdoorly/react-chat-sdk';
import dayjs from 'dayjs';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import React, { useState } from 'react';
import { MessageActionOptions } from '../../../components/IntroSequence';
import { Store } from '../../../store';
import { getIntroField, setIntroField } from '../../../utils';
import { MessageOptionActionType } from '../ChatMessage';
import { Timestamp } from '../Timestamp';

type SendMessageProps = {
  skipMessages: number;
};
type MessageOptionsProps = {
  options: Array<MessageActionOptions>;
  sendNewMessage?: (arg0: SendMessageProps) => void;
  id: string;
  isLastMessage: boolean;
  pastAnswer: string;
  disableAnimation: boolean;
  findExpert?: () => void;
  dateUpdated?: string;
};

export const MessageOptions: React.FC<MessageOptionsProps> = (props) => {
  const { id, isLastMessage, pastAnswer, disableAnimation, findExpert, dateUpdated } = props;
  const { options, sendNewMessage } = props;
  const [animatedIn, setAnimatedIn] = useState<string | undefined>();
  const control = useAnimation();
  const messageSender = useSendMessage();

  return (
    <Flex flexWrap="wrap" ml="28px" mt="4px" w={'calc(100% - 28px)'} pos="relative">
      <AnimatePresence>
        {options.map((option) => {
          const isSelectedOption = animatedIn === option.text || (pastAnswer === option.text && !option.cta);
          const isCancel = option.actionType === MessageOptionActionType.Cancel;
          const isVisible =
            animatedIn || pastAnswer ? isSelectedOption : isCancel && !isLastMessage ? false : true;

          return (
            isVisible && (
              <motion.div
                key={option.text}
                animate={control}
                exit={{ opacity: 0, transition: { duration: 0.05 } }}
                style={{
                  listStyle: 'none',
                  width: pastAnswer === option.text ? '100%' : 'auto',
                  textAlign: pastAnswer === option.text ? 'right' : 'unset'
                }}
                role="row"
              >
                <Button
                  key={option.text}
                  as={isSelectedOption ? 'div' : undefined}
                  variant={
                    isSelectedOption
                      ? 'selected-chat-option'
                      : option?.cta
                      ? 'cta-chat-option'
                      : 'chat-option'
                  }
                  role="gridcell"
                  tabIndex={isSelectedOption ? -1 : 0}
                  cursor={isSelectedOption ? 'default' : 'pointer'}
                  m={'10px 2px 2px 2px'}
                  onClick={() => {
                    if (isSelectedOption) return;
                    if (
                      option.actionType === MessageOptionActionType.AnimateIn ||
                      option.actionType === MessageOptionActionType.CancelPII
                    ) {
                      setIntroField(`introMessage-${id}`, option.text);
                      setIntroField(`messageOptionTime-${id}`, dayjs().toString());
                      setAnimatedIn(option.text);
                      control.start({
                        width: '100%',
                        textAlign: 'right',
                        transition: { duration: 0.4, delay: 0.4 }
                      });

                      if (sendNewMessage) {
                        sendNewMessage({
                          skipMessages: option.cta ? 1 : 0
                        });
                      }

                      if (option.actionType === MessageOptionActionType.CancelPII && !findExpert) {
                        messageSender({ type: 'identify' });
                      }
                    } else if (MessageOptionActionType.Link && option.action) {
                      option.action();
                    } else if (MessageOptionActionType.Cancel) {
                      if (option.action) option.action();
                    }
                  }}
                >
                  <Text
                    transform={isSelectedOption ? 'scale(1.32, 1)' : undefined}
                    pl={isSelectedOption ? `${option.text.length}px` : 0}
                    fontSize={isSelectedOption ? '11px' : 'inherit'}
                    pr={isSelectedOption ? `${option.text.length}px` : 0}
                    aria-label={option.text}
                    role={
                      option.actionType === MessageOptionActionType.Link ||
                      option.actionType === MessageOptionActionType.Cancel
                        ? 'link'
                        : undefined
                    }
                  >
                    {option.text}
                  </Text>
                </Button>
                {isSelectedOption && (
                  <motion.div
                    key="timestamp"
                    initial={disableAnimation ? false : { opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: 0.6 } }}
                  >
                    <Text
                      variant="label-small"
                      textAlign="right"
                      mt="4px"
                      color="scheme.contentTertiary"
                      mr="3px"
                    >
                      <Timestamp time={findExpert ? getIntroField(`messageOptionTime-${id}`) : dateUpdated} />
                    </Text>
                  </motion.div>
                )}
              </motion.div>
            )
          );
        })}
      </AnimatePresence>
    </Flex>
  );
};
